import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { apiClient } from '../../services/authService';
import supplierRequestService from '../../services/supplierRequestService';
import {
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  Archive,
  Package,
  Calendar,
  Euro,
  User
} from 'lucide-react';
import './SupplierRequestsPage.css';

const SupplierRequestsPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { isAdmin } = useAuth();
  const { canEdit, canDelete } = usePermissions();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchRequests();
  }, [currentPage, searchTerm, selectedSupplier, selectedStatus, showArchived]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (selectedSupplier) {
        params.supplier = selectedSupplier;
      }
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (isAdmin && showArchived) {
        params.include_archived = 'true';
      } else if (!showArchived) {
        params.is_active = 'true';
      }

      const response = await supplierRequestService.getAll(params);
      
      if (response.results) {
        setRequests(response.results);
        setTotalCount(response.count || 0);
      } else {
        setRequests(response || []);
        setTotalCount(response?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching supplier requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierRequestService.getSuppliers();
      setSuppliers(response.results || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleDelete = async (requestId) => {
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
          Êtes-vous sûr de vouloir supprimer cette demande ?
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          Cette action est irréversible et supprimera définitivement la demande.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={() => {
              toast.remove(toastId);
              proceedWithDeletion(requestId);
            }}
          >
            Supprimer
          </button>
          <button 
            type="button" 
            className="toast-btn" 
            onClick={() => toast.remove(toastId)}
          >
            Annuler
          </button>
        </div>
      </div>,
      { duration: null }
    );

    const proceedWithDeletion = async (requestId) => {
      try {
        await supplierRequestService.delete(requestId);
        fetchRequests();
        toast.success('Demande supprimée avec succès');
      } catch (error) {
        console.error('Error deleting request:', error);
        toast.error('Erreur lors de la suppression');
      }
    };
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await supplierRequestService.update(requestId, { status: newStatus });
      fetchRequests();
      toast.success('Statut mis à jour avec succès');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING': return 'status-pending';
      case 'APPROVED': return 'status-approved';
      case 'REJECTED': return 'status-rejected';
      case 'COMPLETED': return 'status-completed';
      default: return 'status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING': return 'En attente';
      case 'APPROVED': return 'Approuvée';
      case 'REJECTED': return 'Rejetée';
      case 'COMPLETED': return 'Terminée';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>Chargement des demandes...</span>
      </div>
    );
  }

  return (
    <div className="supplier-requests-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Package size={32} />
            Demandes Fournisseurs
          </h1>
          <p className="page-subtitle">Gestion des demandes fournisseurs</p>
        </div>
        {canEdit('supplier_requests') && (
          <button className="btn-add">
            <Plus size={20} />
            Nouvelle demande
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher une demande..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="search-clear-btn"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
              title="Effacer"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <Package size={18} />
          <select
            value={selectedSupplier}
            onChange={(e) => {
              setSelectedSupplier(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">Tous les fournisseurs</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="PENDING">En attente</option>
            <option value="APPROVED">Approuvées</option>
            <option value="REJECTED">Rejetées</option>
            <option value="COMPLETED">Terminées</option>
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <button
              className={`filter-toggle-btn ${showArchived ? 'active' : ''}`}
              onClick={() => {
                setShowArchived(!showArchived);
                setCurrentPage(1);
              }}
              title={showArchived ? 'Masquer les archives' : 'Afficher les archives'}
            >
              <Archive size={16} />
              <span>{showArchived ? 'Archives' : 'Actives'}</span>
            </button>
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fournisseur</th>
              <th>Produit/Service</th>
              <th>Quantité</th>
              <th>Date demande</th>
              <th>Date livraison</th>
              <th>Montant</th>
              <th>Statut</th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                  Aucune demande trouvée
                </td>
              </tr>
            ) : (
              requests.map((request) => (
                <tr key={request.id}>
                  <td className="name-cell">
                    <div className="supplier-info">
                      <User size={14} />
                      <span>{request.supplier_name}</span>
                    </div>
                  </td>
                  <td>{request.product_name || request.description}</td>
                  <td>{request.quantity}</td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {new Date(request.created_at).toLocaleDateString(i18n.language)}
                    </div>
                  </td>
                  <td>
                    {request.delivery_date ? (
                      <div className="date-cell">
                        <Calendar size={14} />
                        {new Date(request.delivery_date).toLocaleDateString(i18n.language)}
                      </div>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Non définie</span>
                    )}
                  </td>
                  <td>
                    <div className="amount-cell">
                      <Euro size={14} />
                      {request.amount ? `${request.amount.toFixed(2)} €` : 'N/A'}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button className="action-btn" title="Voir les détails">
                        <Eye size={16} />
                      </button>
                      {canEdit('supplier_requests') && (
                        <button 
                          className="action-btn action-edit" 
                          title="Modifier"
                          onClick={() => handleStatusChange(request.id, 'APPROVED')}
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {canDelete('supplier_requests') && (
                        <button 
                          className="action-btn action-delete" 
                          title="Supprimer"
                          onClick={() => handleDelete(request.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > pageSize && (
        <div className="table-footer">
          <div className="results-count">
            {totalCount} demande{totalCount > 1 ? 's' : ''} au total
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Précédent
            </button>
            <span className="pagination-info">
              Page {currentPage} sur {Math.ceil(totalCount / pageSize)}
            </span>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierRequestsPage;
